import { CSSProperties, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { useUpdateEffect } from 'react-use';

import { useRouteTransition } from '~/shared/utils/route-transition/hooks/useRouteTransition';

import styles from './pageLoadingIndicator.module.css';

const initialProgress = 0.1;
const initialIncreaseAmount = 0.2;
const fadeTime = 300;

export const PageLoadingIndicator = () => {
	const { loading } = useRouteTransition();
	const [progress, setProgress] = useState(initialProgress);
	const increaseAmount = useRef(initialIncreaseAmount);
	const timeout = useRef<NodeJS.Timeout | null>(null);
	const [state, setState] = useState<'idle' | 'loading' | 'finishing'>('idle');

	useUpdateEffect(() => {
		if (timeout.current) {
			clearTimeout(timeout.current);
		}
		if (loading) {
			if (state === 'idle') {
				timeout.current = setTimeout(() => {
					setState('loading');
				}, 100);
				increaseAmount.current = initialIncreaseAmount;
				setProgress(initialProgress);
			}
		}
		if (!loading) {
			setState('finishing');
			timeout.current = setTimeout(() => {
				setState('idle');
			}, fadeTime);
		}
	}, [loading]);

	useEffect(() => {
		if (!loading || state === 'idle') {
			return;
		}
		if (timeout.current) {
			clearTimeout(timeout.current);
		}
		if (progress < 0.95) {
			let delay = 0;
			if (progress < 0.7) {
				increaseAmount.current = increaseAmount.current * (0.75 + Math.random() * 0.15);
				delay = 200 + Math.random() * 400;
			} else if (progress < 0.89) {
				increaseAmount.current = 0.0025 + Math.random() * 0.0075;
				delay = 400 + Math.random() * 600;
			} else {
				increaseAmount.current = 0.001 + Math.random() * 0.004;
				delay = 500 + Math.random() * 1000;
			}
			timeout.current = setTimeout(() => {
				setProgress(progress + increaseAmount.current);
			}, delay);
		}
	}, [progress, loading, state]);

	if (state === 'idle') {
		return null;
	}

	return (
		<>
			<div
				className={clsx(styles.root, styles.background, state === 'finishing' && styles.fadingOut)}
				style={{ '--fadeTime': fadeTime } as CSSProperties}
			/>
			<div
				className={clsx(styles.root, state === 'finishing' && styles.fadingOut)}
				style={{ '--fadeTime': fadeTime, '--scaleX': state === 'loading' ? progress : 1 } as CSSProperties}
			/>
		</>
	);
};
